import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, NativeEventEmitter, NativeModules, StyleSheet, View } from 'react-native';
import { InPortal } from 'react-reverse-portal';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { useFocusEffect } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { TWCBackHandler } from '@components';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { PortalOrigin } from 'rn-native-portals';

import {
  alertDetailsPageTestIDs,
  AlertModalTypes,
  alertsModalPageTestIDs,
  currentWeatherConditionsPageTestIDs,
  dailyWeatherPageTestIDs,
  hourlyWeatherPageTestIDs,
  livePageTestIDs,
  loadingPageTestIDs,
  locationsIDs,
  mapPageTestIDs,
  menuTestIDs,
  myWeatherPageTestIDs,
  Theme,
} from '@constants';
import { helpers } from '@helpers';
import {
  useAlertTrigger,
  useANFTrigger,
  useAppContextReference,
  useDateTime,
  useDebugTrigger,
  useDeepLinkURL,
  useLiveStreamSelector,
  useNetworkStatus,
  useScreenReaderEnabled,
  useTWCApi,
  useVideoNodePortal,
} from '@hooks';
import { navigationService } from '@services';
import { alertsHeadlinesState, anfState, hdmiConnectedState, liveVideoPlayerState, userSettingsState } from '@store';

import { initialRoute, mainMenuScreens } from './screens/baseScreens';
import { screens } from './screens/screens';
import { useGooglePalUpdate } from './hooks';

import { MainMenu } from '../components/MainMenu/MainMenu';
import { SegmentIdentifyUser } from '../components/segment/SegmentIdentifyUser';
import { VideoPlayer } from '../components/video/VideoPlayer';

const Tab = createBottomTabNavigator();
const Stack = createStackNavigator();

const preventBackPress = () => true;

const identifyUser = _userSettings => {
  if (_userSettings?.isPaidSubscriber || _userSettings?.isCableSubscriber) {
    SegmentIdentifyUser(_userSettings, _userSettings?.isCableSubscriber ?? false);
  }
};

const MainMenuTabs = () => {
  useFocusEffect(
    useCallback(() => {
      TWCBackHandler.addEventListener('hardwareBackPress', preventBackPress);
      return () => TWCBackHandler.removeEventListener('hardwareBackPress', preventBackPress);
    }, []),
  );

  return (
    <Tab.Navigator
      animationEnabled={false}
      backBehavior={'none'}
      initialLayout={{ width: Theme.px(1920), height: Theme.px(1080) }}
      initialRouteName={initialRoute}
      sceneContainerStyle={{ width: Theme.px(1920), height: Theme.px(1080) }}
      screenOptions={{
        headerShown: false,
        gestureEnabled: false,
        animationEnabled: false,
        unmountInactiveScreen: true,
        detachPreviousScreen: true,
      }}
      // eslint-disable-next-line react/no-unstable-nested-components
      tabBar={properties => <MainMenu {...properties} />}
    >
      {mainMenuScreens.map(screen => (
        <Tab.Screen
          key={screen.name}
          animationEnabled={false}
          component={screen.component}
          initialLayout={{ width: Theme.px(1920), height: Theme.px(1080) }}
          name={screen.name}
          options={screen.options}
        />
      ))}
    </Tab.Navigator>
  );
};

const LiveVideoPages = new Set([
  livePageTestIDs.livePage,
  myWeatherPageTestIDs.myWeatherPage,
  currentWeatherConditionsPageTestIDs.currentWeatherConditionsPage,
  dailyWeatherPageTestIDs.dailyWeatherPage,
  hourlyWeatherPageTestIDs.hourlyWeatherPage,
  mapPageTestIDs.mapPage,
  mapPageTestIDs.changeMapPage,
  alertDetailsPageTestIDs.alertDetailsPage,
]);

const shouldPreventANFFromRoute = route => {
  const modalScreens = screens.filter(screen => !(screen.options?.anfCanOverride ?? true));

  return (
    modalScreens.findIndex(screen => screen.name === route) >= 0 ||
    Object.values(AlertModalTypes).includes(route) ||
    route === locationsIDs.changeLocationModal
  );
};

const shouldPreventAlertsFromRoute = route => {
  const modalScreens = screens.filter(screen => !(screen.options?.anfCanOverride ?? true));

  return (
    modalScreens.findIndex(screen => screen.name === route) >= 0 ||
    Object.values(AlertModalTypes).includes(route) ||
    route === locationsIDs.changeLocationModal
  );
};

export const AppNavigator = () => {
  useScreenReaderEnabled();
  useNetworkStatus();
  useDateTime({ use12HourExtension: false });
  useTWCApi();
  useDebugTrigger();
  useDeepLinkURL();
  useLiveStreamSelector();
  useGooglePalUpdate();

  const liveVideoPortalNode = useVideoNodePortal();
  const _appState = useRef(AppState.currentState);

  const mainMenuReference = useAppContextReference(menuTestIDs.mainMenu);
  const lastRoute = useAppContextReference('lastRoute');
  const menuPage = useRef(null);

  const setLiveVideoState = useSetRecoilState(liveVideoPlayerState);
  const currentANFState = useRecoilValue(anfState);
  const userSetings = useRecoilValue(userSettingsState);
  const [alerts, setAlerts] = useRecoilState(alertsHeadlinesState);

  useANFTrigger(currentANFState, shouldPreventANFFromRoute(lastRoute.current));

  const newAlert = useAlertTrigger(alerts, shouldPreventAlertsFromRoute(lastRoute.current));

  const [liveVideoLocation, setNewLiveVideoLocation] = useState(null);

  const setLiveVideoLocation = page => {
    setTimeout(() => {
      setNewLiveVideoLocation(page);
    }, 1);
  };

  useEffect(() => {
    const handleChange = newState => {
      if (/inactive|background/.test(_appState.current) && newState === 'active') {
        identifyUser(userSetings);
      }
      _appState.current = newState;
    };

    if (helpers.isAndroid) {
      AppState.addEventListener('change', handleChange);
    }

    return () => {
      if (helpers.isAndroid) {
        AppState.removeEventListener('change', handleChange);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_appState.current, userSetings]);

  // HDMI connection listener
  const hdmiListenerStarted = useRef(false);

  const [HDMIConnected, setHDMIConnected] = useRecoilState(hdmiConnectedState);

  useEffect(() => {
    const listeners = [];

    if (!hdmiListenerStarted.current) {
      if (helpers.isAndroid) {
        helpers.startConnectionListener();

        const nativeEventEmitter = new NativeEventEmitter(NativeModules.AudioSoundManagerModule);
        const connectedListener = nativeEventEmitter.addListener('DeviceConnected', () => {
          console.log('HDMI device connected');
          // Else let ok button trigger re-connection logic
          if (lastRoute.current !== AlertModalTypes.disconnected) {
            setHDMIConnected(true);
          }
        });

        const disconnectedListener = nativeEventEmitter.addListener('DeviceRemoved', () => {
          console.log('HDMI device removed');
          setHDMIConnected(false);
        });

        listeners.push(connectedListener, disconnectedListener);
      }
      if (helpers.isTizen) {
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) {
            setHDMIConnected(false);
          } else {
            setHDMIConnected(true);
            if (lastRoute.current === AlertModalTypes.disconnected) {
              navigationService.goBack();
            }
          }
        });
      }
      hdmiListenerStarted.current = true;
    }
    return () => {
      for (const listener of listeners) {
        listener.remove();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!HDMIConnected && liveVideoLocation !== null) {
      navigationService.navigate(alertsModalPageTestIDs.alertsModalPage, {
        type: AlertModalTypes.disconnected,
      });
      setLiveVideoLocation(null);
    }
  }, [HDMIConnected, liveVideoLocation, menuPage]);

  // Alerts
  useEffect(() => {
    if (newAlert) {
      const shownAlerts = [newAlert.identifier];

      shownAlerts.push(alerts.shownAlerts);
      setAlerts({
        ...alerts,
        shownAlerts,
      });

      // Show Alert Overlay
      navigationService.navigate(alertsModalPageTestIDs.alertsModalPage, {
        type: AlertModalTypes.overlay,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newAlert, alerts]);

  useEffect(() => {
    if (!liveVideoLocation) {
      setLiveVideoState(liveVideoState => ({
        ...liveVideoState,
        stopped: true,
        width: 0,
        height: 0,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveVideoLocation]);

  const LiveVideoPlayer = helpers.isAndroid ? require('../components/video/VideoPlayer').LiveVideoPlayer : null;

  return (
    <View collapsable={!helpers.isAndroid} style={[styles.navigator]}>
      <>
        {helpers.isWeb ? (
          <InPortal isScreenFocused={false} node={liveVideoPortalNode}>
            <VideoPlayer isScreenFocused={false} />
          </InPortal>
        ) : (
          <PortalOrigin collapsable={false} destination={'liveVideoPlayer'} style={styles.nativePortal}>
            <LiveVideoPlayer />
          </PortalOrigin>
        )}
      </>

      <Stack.Navigator
        detachInactiveScreens={!helpers.isAndroid}
        initialRouteName={loadingPageTestIDs.loadingPage}
        screenListeners={{
          state: event => {
            if (event?.data?.state?.routes) {
              // Get the current page in the stack navigator
              const currentRoute = event.data.state.routes[event.data.state.routes.length - 1];
              const currentRouteName = currentRoute.name;
              const currentRouteParameters = currentRoute.params;

              menuPage.current = null;
              if (LiveVideoPages.has(currentRouteName)) {
                setLiveVideoLocation(currentRouteName);
              } else if (currentRouteName === 'MainMenu') {
                const menuState = currentRoute.state;

                if (menuState) {
                  const menuRoutes = menuState.routes;
                  const currentMenuRoute = menuRoutes[menuState.index];

                  if (currentMenuRoute) {
                    const lastMenuRouteName = currentMenuRoute.name;

                    menuPage.current = lastMenuRouteName;
                    if (LiveVideoPages.has(lastMenuRouteName)) {
                      setLiveVideoLocation(lastMenuRouteName);
                    } else {
                      setLiveVideoLocation(null);
                    }
                  }
                } else {
                  setLiveVideoLocation(livePageTestIDs.livePage);
                }
              } else if (currentRouteName !== alertsModalPageTestIDs.alertsModalPage) {
                setLiveVideoLocation(null);
              }

              // If we are returning from the exit modal, show the menu here
              // This overrides default behavior, which focuses the page
              if (lastRoute.current === AlertModalTypes.exit && currentRouteName === 'MainMenu') {
                helpers.FOCUS_NOW(mainMenuReference);
              }

              // Set the route to keep track of where we navigated from. For alert modal, use
              // the modal type for more specific behavior
              lastRoute.current =
                currentRouteName === alertsModalPageTestIDs.alertsModalPage
                  ? currentRouteParameters.type
                  : currentRouteName;
            }
          },
          beforeRemove: event => {
            // Do something with the state
            // can prevent back here
            // console.log(`beforeRemove changed`, event);
            if (liveVideoLocation) {
              switch (liveVideoLocation) {
                case currentWeatherConditionsPageTestIDs.currentWeatherConditionsPage:
                case dailyWeatherPageTestIDs.dailyWeatherPage:
                case hourlyWeatherPageTestIDs.hourlyWeatherPage:
                case mapPageTestIDs.mapPage:
                  setLiveVideoLocation(myWeatherPageTestIDs.myWeatherPage);
              }
            }
          },
          focus: event => {
            // Do something with the state
            // console.log(`focus changed`, event);
          },
          blur: event => {
            // Do something with the state
            // console.log(`blur changed`, event);
          },
        }}
        screenOptions={{
          headerShown: false,
          gestureEnabled: false,
          animationEnabled: false,
          unmountInactiveScreen: !helpers.isAndroid,
          detachPreviousScreen: true,
        }}
      >
        <Stack.Screen key={'MainMenu'} component={MainMenuTabs} name={'MainMenu'} />
        {screens.map(screen => (
          <Stack.Screen
            key={screen.name}
            component={screen.component}
            listeners={{}}
            name={screen.name}
            options={screen.options}
          />
        ))}
      </Stack.Navigator>
    </View>
  );
};

const styles = StyleSheet.create({
  app: {
    flex: 1,
    flexDirection: 'row',
    height: Theme.px(1080),
    width: Theme.px(1920),
  },
  nativePortal: {
    height: 1,
    left: -1,
    position: 'absolute',
    top: -1,
    width: 1,
  },
  navigator: {
    height: Theme.px(1080),
    width: Theme.px(1920),
  },
});
